import { useState, useEffect, useRef } from "react";
import { FaTrash, FaEdit, FaCopy, FaGripVertical } from "react-icons/fa";
import Button from "../../../components/Button.js";
import TextInput from "../../../components/TextInput";
import TextArea from "../../../components/TextArea";
import Sortable from "sortablejs";
import { current } from "@reduxjs/toolkit";
import { convertToPng } from "../../../utils/imageUtils.js";

// ===== UTILITAIRES DE BASE =====

/*** Retourne un objet action vide avec la structure par défaut
 
 * @returns {Object} Action vide avec tous les champs initialisés
 */
export function getEmptyAction() {
  return {
    feedbackItemTemplateID: 0,
    actionTitle: "",
    actionDesc: "",
    actionImg: "",
    actionHologram: "",
    actionHologramOffsetPos: { x: 0, y: 0, z: 0 },
    actionHologramOffsetRot: { x: 0, y: 0, z: 0 },
    actionAudioText: "",
    actionAudio: "",
    audioIsComment: true,
    actionFeedbacks: [],

  };
}
/**
 * Remet à zéro les états du formulaire
 */
function resetFormStates({
  setCurrentAction,
  setEditingActionIndex,
  setPendingImg,
  setPendingAudio,
  setPendingHologram
}) {
  setCurrentAction(getEmptyAction());
  setEditingActionIndex(null);
  setPendingImg(null);
  setPendingAudio(null);
  setPendingHologram(null);
}


// fonction pour le défilement vers la zone de contenu
const scrollToContentArea = (ElementId) => {
  setTimeout(() => {
    const anchor = document.getElementById(ElementId);
    if (anchor) anchor.scrollIntoView({ behavior: "smooth" });
  }, 100);
};

/**
 * Ajoute un ID temporaire unique à une action si elle n'en a pas déjà un
 * @param {Object} action - L'action à traiter
 * @returns {Object} L'action avec un actionID
 */
export function addTmpIdToAction(action) {
  if (!action.actionID) {
    return {
      ...action,
      actionID: Date.now() + Math.random(), // Ajoute un ID temporaire unique au cas où...
    };
  }
  return action;
}

/*** Réindexe les actionID des actions avec des valeurs séquentielles
 
 * @param {Array} actions - Tableau d'actions
 * @returns {Array} Actions avec actionID réindexés
 */
export function reindexActionTmpIds(actions) {
  return actions.map((action, idx) => ({
    ...action,
    actionID: idx + 1
  }));
}

// ===== GESTION DES FEEDBACKS =====

/*** Retourne un feedback initial vide
 
 * @returns {Object} Feedback vide avec la structure par défaut
 */
export function getInitialFeedback() {
  return {
    feedbackPrefab: [],
    feedbackPos: { x: 0, y: 0, z: 0 },
    feedbackRot: { x: 0, y: 0, z: 0 },
    feedbackPrefabScale: "1",
  };
}

// ===== VALIDATION =====

/*** Valide une action
 
 * @param {Object} action - Action à valider
 * @returns {Object} Résultat de validation { isValid: boolean, errors: Array }
 */
export function validateAction(action) {
  const errors = [];

  if (!action.actionTitle || action.actionTitle.trim() === '') {
    errors.push('Le titre de l\'action est obligatoire');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

// ===== COMPOSANT ACTION MANAGER =====

/*** Composant principal pour la gestion des actions
 * Contient toute la logique et l'interface utilisateur pour les actions
 */
const ActionManage = ({
  Contents,
  setContents,
  editingStepIndex,
  folderName,
  getNextStepID,
  setMessage,
  onUpload,
  onDelete,
  detailsRef,
}) => {
  const actionsListRef = useRef(null);
  // États locaux pour la gestion des actions
  const [editingActionIndex, setEditingActionIndex] = useState(null);
  const [currentAction, setCurrentAction] = useState(getEmptyAction());
  const [currentFeedback, setCurrentFeedback] = useState(getInitialFeedback());
  const [editingFeedback, setEditingFeedback] = useState(false);
  const [editingFeedbackIndex, setEditingFeedbackIndex] = useState(null);

  // État pour les fichiers en attente
  const [pendingImg, setPendingImg] = useState(null);
  const [pendingAudio, setPendingAudio] = useState(null);
  const [pendingHologram, setPendingHologram] = useState(null);
  const [previewImgUrl, setPreviewImgUrl] = useState(null);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);

  const [isImgModalOpen, setIsImgModalOpen] = useState(false);
  const [imgModalSrc, setImgModalSrc] = useState(null);
  const openImgModal = (src) => {
    setImgModalSrc(src);
    setIsImgModalOpen(true);
  };

  const closeImgModal = () => {
    setIsImgModalOpen(false);
    setImgModalSrc(null);
  };

  const [showTransformMenu, setShowTransformMenu] = useState(false);

  // Hook pour initialiser SortableJS sur la liste des actions
  useEffect(() => {
    if (!actionsListRef.current || !(Contents?.stepActions?.length > 1)) return;
    // Détruire l'instance précédente si elle existe
    if (actionsListRef.current._sortable) {
      actionsListRef.current._sortable.destroy();
      actionsListRef.current._sortable = null;
    }
    actionsListRef.current._sortable = Sortable.create(actionsListRef.current, {
      animation: 150,
      handle: '.drag-handle',
      onEnd: (evt) => {
        if (evt.oldIndex === evt.newIndex) return;
        const nodes = Array.from(actionsListRef.current.children);
        const newOrder = nodes.map(node => node.getAttribute('data-id'));
        const newActions = newOrder
          .map(id => Contents.stepActions.find(a => String(a.actionID) === String(id)))
          .filter(Boolean);
        setContents(prev => ({ ...prev, stepActions: newActions }));
      },
    });
    // Nettoyage
    return () => {
      if (actionsListRef.current && actionsListRef.current._sortable) {
        actionsListRef.current._sortable.destroy();
        actionsListRef.current._sortable = null;
      }
    };
  }, [Contents?.stepActions, setContents]);

  /* Synchronise currentAction avec l'action à éditer */
  useEffect(() => {
    if (editingActionIndex !== null && Array.isArray(Contents.stepActions)) {
      const actionToEdit = Contents.stepActions.find(a => a.actionID === editingActionIndex);
      if (actionToEdit) {
        setCurrentAction({ ...actionToEdit });
      }
    }
    if (editingActionIndex === null) {
      setCurrentAction(getEmptyAction());
    }
  }, [editingActionIndex, Contents.stepActions]);

  useEffect(() => {
    if (!pendingImg) {
      setPreviewImgUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingImg);
    setPreviewImgUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingImg]);

  useEffect(() => {
    if (!pendingAudio) {
      setPreviewAudioUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingAudio);
    setPreviewAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingAudio]);

  // Fonctions locales pour la gestion des actions
  const handleCancelAction = () => {
    resetFormStates({
      setCurrentAction,
      setEditingActionIndex,
      setPendingImg,
      setPendingAudio,
      setPendingHologram,
    });    /*setCurrentAction(getEmptyAction());
    setEditingActionIndex(null);*/
    detailsRef.current?.removeAttribute("open");
  };

  const handleAddAction = async () => {
    const newAction = {
      ...currentAction,
      //ajoute un tmpId = nombre d'actions du step en cours d'édition + 1
      // si actionID existe, on l'incrémente
      actionID: currentAction.actionID ? currentAction.actionID : (Contents.stepActions || []).length + 1,

    };
    // Upload des fichiers et mise à jour des noms
    if (pendingImg) newAction.actionImg = pendingImg;
    if (pendingAudio) newAction.actionAudio = pendingAudio;
    if (pendingHologram) newAction.actionHologram = pendingHologram;

    setContents(prev => {
      const updatedContents = {
        ...prev,
        stepActions: editingActionIndex !== null
          ? prev.stepActions.map(action =>
            action.actionID === editingActionIndex ? newAction : action
          )
          : [...(prev.stepActions || []), newAction]
      };
      detailsRef.current?.removeAttribute("open");
      console.log("Contenu mis à jour :", updatedContents);
      return updatedContents;
    });

    resetFormStates({
      setCurrentAction,
      setEditingActionIndex,
      setPendingImg,
      setPendingAudio,
      setPendingHologram
    });
    setMessage({ type: "success", text: "Action enregistrée avec succès !" });
  };

  const handleDuplicateAction = (actionTmpId) => {
    const actionToDuplicate = Contents.stepActions.find(action => action.actionID === actionTmpId);
    if (!actionToDuplicate) return;

    const duplicatedAction = {
      ...actionToDuplicate,
      actionID: (Contents.stepActions || []).length + 1,
      actionTitle: `${actionToDuplicate.actionTitle} (copie)`
    };

    setContents(prev => ({
      ...prev,
      stepActions: [...(prev.stepActions || []), duplicatedAction]
    }));

    setMessage({ type: "success", text: "Action dupliquée avec succès !" });
  };

  const handleDeleteAction = (actionTmpId) => {
    if (!window.confirm("⚠️ Êtes-vous sûr de vouloir supprimer cette action ?\n\n❌ [" + (Contents.stepActions.find(a => a.actionID === actionTmpId)?.actionTitle || "") + "]")) {
      return;
    }

    const actionToDelete = Contents?.stepActions.find((action) => action.actionID === actionTmpId);

    if (actionToDelete) {
      if (actionToDelete.actionImg) {
        deleteActionFile({ fieldName: "actionImg", confirmed: true });
      }

      if (actionToDelete.actionAudio) {
        deleteActionFile({ fieldName: "actionAudio", confirmed: true });
      }

      if (actionToDelete.actionHologram) {
        deleteActionFile({ fieldName: "actionHologram", confirmed: true });
      }
    }

    setContents(prev => ({
      ...prev,
      stepActions: prev.stepActions.filter(action => action.actionID !== actionTmpId)
    }));

    if (editingActionIndex === actionTmpId) {
      resetFormStates({
        setCurrentAction,
        setEditingActionIndex,
        setPendingImg,
        setPendingAudio,
        setPendingHologram
      });
    }

    setMessage({ type: "success", text: "Action supprimée avec succès !" });
  };

  const handleAddFeedback = () => {
    setCurrentAction((prev) => {
      let newFeedback =
        editingFeedback == true ?
          prev.actionFeedbacks.map((fb, idx) =>
            idx === editingFeedbackIndex ?
              { ...fb, ...currentFeedback } :
              fb
          )
          : prev.actionFeedbacks ? [...prev.actionFeedbacks, currentFeedback] : [currentFeedback];
      return {
        ...prev,
        actionFeedbacks: newFeedback
      };
    });
    setCurrentFeedback(getInitialFeedback());
    setEditingFeedbackIndex(null);
    setEditingFeedback(false);
  };

  const handleEditFeedback = (idx) => {
    const feedbackToEdit = currentAction.actionFeedbacks[idx];
    if (feedbackToEdit) {
      setCurrentFeedback(feedbackToEdit);
      setEditingFeedbackIndex(idx);
      setEditingFeedback(true);
    }
  };

  const handleCancelFeedbackEdit = () => {
    setCurrentFeedback(getInitialFeedback());
    setEditingFeedbackIndex(null);
    setEditingFeedback(false);
  };


  const handleRemoveFeedback = (idx) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet indicateur ?")) {
      return;
    }
    setCurrentAction((prev) => ({
      ...prev,
      actionFeedbacks: prev.actionFeedbacks.filter((_, i) => i !== idx),
    }));
  };

  const handleFeedbackPrefabChange = (type, value) => {
    setCurrentFeedback(prev => {
      if (type === "main") {
        return { ...prev, feedbackPrefab: [value] };
      }
      if (type === "direction") {
        const base = [prev.feedbackPrefab[0], value, prev.feedbackPrefab[2], ...prev.feedbackPrefab.slice(3)].filter(Boolean);
        return { ...prev, feedbackPrefab: base };
      }
      if (type === "orientation") {
        const arr = [...prev.feedbackPrefab];
        arr[2] = value;
        return { ...prev, feedbackPrefab: arr.slice(0, 3).filter(Boolean) };
      }
      if (type === "distance") {
        const base = prev.feedbackPrefab.filter(v => v !== "distance");
        if (value) base.push("distance");
        return { ...prev, feedbackPrefab: base };
      }
      // ✅ Cas générique pour feedbackPrefabScale et autres champs directs
      if (type in prev) {
        return { ...prev, [type]: value };
      }
      return prev;
    });
  };

  const deleteActionFile = async ({ fieldName, confirmed }) => {
    if (!confirmed) {
      if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce fichier ?\n\n" + "👉 " + fieldName))
        return;
    }
    let actionIndex = 0;
    if (Array.isArray(Contents.stepActions)) {
      actionIndex = Contents.stepActions.findIndex(a => a.actionID === currentAction.actionID);
      if (actionIndex === -1) {
        actionIndex = Contents.stepActions.length;
      }
    }

    setCurrentAction({
      ...currentAction
      , actionImg: "",
    })

    setContents(prev => ({
      ...prev,
      stepActions: prev.stepActions.map(action => action.actionID === currentAction.actionID ? { ...action, [fieldName]: "" } : action)
    }));
  };

  // Rendu du composant
  return (
    <div className="mt-4 p-4 border-primary rounded" style={{ borderStyle: 'dashed', borderWidth: '1px' }}>
      {/* LISTE DES ACTIONS */}
      {(Contents?.stepActions?.length || 0) > 0 ? (
        <div alt="liste des actions">
          <h3 className="text-primary text-center">Liste des actions</h3>
          <p className="small text-center">Actions à réaliser lors de cette étape.</p>
          <div ref={actionsListRef}>
            {(Contents?.stepActions || []).map((action) => (
              <div alt="drag handle"
                className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
                key={action.actionID}
                data-id={action.actionID}
              >
                <span className="drag-handle me-2" style={{ cursor: 'grab' }} title="Déplacer">
                  <FaGripVertical />
                </span>
                <div alt="Action title" className="flex-grow-1">
                  <strong>
                    {action.actionTitle || "⚠️ Sans titre"}
                  </strong>
                  <div className="text-muted small">
                    {action.actionID && `(ID: ${action.actionID}) `}{action.actionDesc}
                  </div>
                </div>
                <div alt="Boutons Editer, Dupliquer, Supprimer les actions">
                  <button alt="Editer l'action"
                    type="button"
                    className="btn btn-sm btn-outline-primary me-2"
                    onClick={() => {
                      setEditingActionIndex(action.actionID);
                      if (detailsRef.current) {
                        detailsRef.current.setAttribute("open", true);
                        detailsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                      }
                    }}
                    title="Modifier l'action"
                  >
                    <FaEdit />
                  </button>
                  <button alt="Dupliquer l'action"
                    type="button"
                    className="btn btn-sm btn-outline-secondary me-2"
                    onClick={() => handleDuplicateAction(action.actionID)}
                    title="Dupliquer l'action"
                  >
                    <FaCopy />
                  </button>
                  <button alt="Supprimer l'action"
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDeleteAction(action.actionID)}
                    title="Supprimer l'action"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div alt="Pas d'action">
          <h3 className="text-primary text-center">Pas d'action</h3>
          <p className="small text-center"><b>Aucune action pour cette étape</b></p>
        </div>
      )}

      {/* FORMULAIRE D'ÉDITION DES ACTIONS */}
      <details ref={detailsRef} id="actionEditForm">
        <summary className="fw-bold fs-5 text-primary text-center mb-4">
          {editingActionIndex !== null ? (
            <>Modifier l'action {Contents.stepActions.find(a => a.actionID === editingActionIndex)?.actionTitle && (
              <span> "{Contents.stepActions.find(a => a.actionID === editingActionIndex).actionTitle}"</span>
            )}</>
          ) : (Contents?.stepActions?.length || 0) > 0 ? (
            <>Ajouter une nouvelle action</>
          ) : (
            <>Créer une nouvelle action</>
          )}
        </summary>
        <div className="border rounded p-4" alt="Edition de l'action">
          <div className="row">
            <div className="col-md-4" alt="titre de l'action">
              <TextInput
                label="Titre (obligatoire)"
                value={currentAction.actionTitle}
                placeholder={"Nom de l'action à réaliser"}
                onChange={(e) =>
                  setCurrentAction({
                    ...currentAction,
                    actionTitle: e.target.value,
                  })
                }
                className="mb-2"
              />
            </div>
            <div alt="Description de l'action" className="col-md-4">
              <TextArea
                label="Description"
                value={currentAction.actionDesc}
                placeholder={"Détails de l'action à réaliser"}
                onChange={(e) =>
                  setCurrentAction({
                    ...currentAction,
                    actionDesc: e.target.value,
                  })
                }
                className="mb-2"
              />
            </div>
            <div alt="audio text de l'action" className="col-md-4">
              <TextArea
                label="Texte Audio"
                value={currentAction.actionAudioText || ""}
                onChange={(e) =>
                  setCurrentAction({
                    ...currentAction,
                    actionAudioText: e.target.value,
                  })
                }
                className="mb2"
              />
            </div>
          </div>
          <div className="row mb-4">
            <div alt="image de l'action" className="col-md-4" title="Image de l'action en cours (optionnelle).
            ⚠️ Actualise et remplace l'image de l'étape dès que l'action est activée.">
              <label className="form-label">
                <i className="mdi mdi-image-frame" /> Image de l'action (png/jpg) ℹ️
              </label>
              {currentAction.actionImg ? (
                <div alt="upload file" className="d-flex align-items-center">
                  <input alt="replace file"
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    className="form-control"
                    style={{ width: "140px" }}
                    title='♻️ Remplacer le fichier existant'
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const pngFile = await convertToPng(file);
                        setPendingImg(pngFile);
                      }
                    }}
                  />
                  <div alt="delete file" className="d-flex ">
                    {(typeof currentAction.actionImg !== 'string' ||
                      currentAction.actionImg.includes('[object')) ? (
                      <div className="text-danger small fw-bold ms-2">
                        [Fichier présent]<br />en attente de sauvegarde...
                      </div>
                    ) : (
                      <img
                        src={previewImgUrl ? previewImgUrl : `/api/data/${folderName}/${currentAction.actionImg}`}
                        alt="Vignette"
                        style={{
                          maxWidth: "60px",
                          maxHeight: "60px",
                          marginLeft: "10px",
                          borderRadius: "4px",
                          border: "1px solid #ccc",
                          cursor: "zoom-in"
                        }}
                        onClick={() =>
                          openImgModal(
                            previewImgUrl
                              ? previewImgUrl
                              : `/api/data/${folderName}/${currentAction.actionImg}`
                          )
                        }
                      />

                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={() => { deleteActionFile({ fieldName: "actionImg", confirmed: false }) }}
                      title={`❌ Supprimer le fichier existant\n (${currentAction.actionImg})`}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <input alt="upload file"
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    className="form-control"
                    title='📥 Ajouter un fichier'
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const pngFile = await convertToPng(file);
                        setPendingImg(pngFile);
                      }
                    }}
                  />
                  {previewImgUrl && (
                    <img
                      src={previewImgUrl}
                      alt="Vignette"
                      style={{ maxWidth: "60px", maxHeight: "60px", marginTop: "10px", borderRadius: "4px", border: "1px solid #ccc" }}
                    />
                  )}
                </>
              )}
            </div>
            {/* ------- GESTION DE L'HOLOGRAMME ---------- */}
            <div alt="Hologramme de l'action" className="col-md-4">
              <label className="form-label"
                title="[OPTION] Permet d'ajouter un hologramme d'action
              sur le modèle principal.
              👉 Le pivot de l'objet 3D est le même que celui du modèle principal
              afin de garantir un placement correct.">
                <i className="mdi mdi-cube-outline" /> Hologramme d'action (glb) ℹ️
              </label>
              {currentAction.actionHologram ? (
                <div alt="Action hologram" className="d-flex align-items-center">
                  <input alt="replace file"
                    type="file"
                    accept=".glb"
                    className="form-control"
                    style={{ width: "140px" }}
                    title='♻️ Remplacer le fichier existant'
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setPendingHologram(file);
                      }
                    }}
                  />
                  <div alt="delete file">
                    <span className="text-primary small fw-bold ms-2">[Fichier existant]</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger ms-2"
                      //onClick={() => handleDeleteFile("stepHologram")}
                      onClick={() => deleteActionFile({ fieldName: "actionHologram", confirmed: false })}
                      title={`❌ Supprimer le fichier existant\n (${currentAction.actionHologram})`}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ) : (
                <input alt="replace file"
                  type="file"
                  accept=".glb"
                  className="form-control"
                  title='📥 Ajouter un fichier'
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setPendingHologram(file);
                    }
                  }}
                />
              )}
              {/* interactable / non-interactable */}
              {/*{(!!pendingHologram || !!Contents.stepHologram) && (
                <div alt="gestion de l'hologramme" className="row">
                  <div alt="interactable" className="col-md-8">
                    <select
                      className="form-select form-select-sm mt-2"
                      value={Contents.stepHologramInteractable ? "true" : "false"}
                      onChange={(e) => handleChange("stepHologramInteractable", e.target.value === "true")}
                    >
                      <option value="false" title="L'hologramme remplace temporairement l'hologramme principal
                      (ex : un ordinateur sans sa plaque de fond.)">Hologramme fixe</option>
                      <option value="true" title="L'hologramme est manipulable par l'utilisateur.
                      Dédié aux petits objets sur lequel portent les actions
                      (ex : un disque sur lequel on doit fixer un chassis)">Hologramme manipulable</option>
                    </select>
                  </div>
                  {!Contents.stepHologramInteractable && (
                  <div alt="pos/rot" className="col-md-4 mt-3">
                    <span className="me-2 small text-secondary" title="position X,Y,Z">
                      [{Contents.stepHologramOffsetPos.x},{Contents.stepHologramOffsetPos.y},{Contents.stepHologramOffsetPos.z}]
                    </span>
                    <span className="me-2 small text-secondary" title="rotation X,Y,Z">
                      [{Contents.stepHologramOffsetRot.x},{Contents.stepHologramOffsetRot.y},{Contents.stepHologramOffsetRot.z}]
                    </span>
                  </div>
                  )}
                </div>
              )}*/}
            </div>
            {/* ---- Gestion de l'AUDIO ---- */}
            <div alt="Audio de l'action" className="col-md-4">
              <label className="form-label" title="Fichier audio du commentaire.">
                <i className="mdi mdi-volume-high" /> Audio (ogg) ℹ️
              </label>
              {currentAction.actionAudio ? (
                <div alt="upload file" className="d-flex flex-column align-items-start">
                  {/* Upload désactivé tant qu'un fichier existe */}
                  {/*
                  <input alt="replace file"
                    type="file"
                    accept=".ogg"
                    className="form-control"
                    title='📥 Choisir un fichier (remplacer)'
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setPendingAudio(file);
                      }
                    }}
                  />
                  */}
                  {previewAudioUrl && (
                    <audio
                      controls
                      src={previewAudioUrl}
                      style={{ maxWidth: "100%", marginTop: "-10px" }}
                    >
                      Votre navigateur ne supporte pas l'audio.
                    </audio>
                  )}
                  {!previewAudioUrl && (
                    <div alt="delete file">
                      <audio
                        controls
                        src={`/api/data/${folderName}/${currentAction.actionAudio}`}
                        style={{ maxWidth: "100%", marginTop: "-10px" }}
                        onLoadedData={e => console.log('Audio URL:', e.target.src)}
                      >
                        Votre navigateur ne supporte pas l'audio.
                      </audio>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger ms-2"
                        style={{ marginTop: "-50px" }}
                        onClick={() => deleteActionFile({ fieldName: "actionAudio", confirmed: false })}
                        title={`❌ Supprimer le fichier existant\n(${currentAction.actionAudio})`}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <input
                    alt="upload file"
                    id="upload"
                    type="file"
                    accept=".ogg"
                    className="form-control"
                    title='📥 Ajouter un fichier audio'
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setPendingAudio(file);
                      }
                    }}
                  />
                  {previewAudioUrl && (
                    <audio
                      controls
                      src={previewAudioUrl}
                      style={{ maxWidth: "100%" }}
                    >
                      Votre navigateur ne supporte pas l'audio.
                    </audio>
                  )}
                </>
              )}
            </div>


          </div>

          {/* Indicateurs (feedbacks) */}
          <div className="border border-primary rounded p-3">
            <h3 className="text-primary text-center mb-4">Indicateurs</h3>
            <div className="row">
              <div className="col" alt="left">
                <div className="row">
                  <div alt="Forme de l'indicateur" className="col-md-8">
                    <label className="form-label"><i className="mdi mdi-map-marker-outline" />Type d'indicateur</label>

                    {/* Niveau 1 : type principal */}
                    <div className="d-flex gap-2 mb-2 flex-wrap">
                      {["zone", "niveau", "aplomb", "point"].map(opt => (
                        <button
                          key={opt}
                          type="button"
                          className={`btn btn-sm ${currentFeedback.feedbackPrefab[0] === opt ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => handleFeedbackPrefabChange("main", opt)}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>

                    {/* Niveau 2 : options spécifiques à "point" */}
                    {currentFeedback.feedbackPrefab[0] === "point" && (
                      <div className="ps-2 border-start border-primary mt-2">

                        {/* in / out */}
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                          <small className="text-secondary">Direction :</small>
                          {["in", "out"].map(opt => (
                            <button
                              key={opt}
                              type="button"
                              className={`btn btn-sm ${currentFeedback.feedbackPrefab[1] === opt ? "btn-primary" : "btn-outline-secondary"}`}
                              onClick={() => handleFeedbackPrefabChange("direction", opt)}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>

                        {/* hor / ver */}
                        <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                          <small className="text-secondary">Orientation :</small>
                          {[["vertical", "Vertical"], ["horizontal", "Horizontal"]].map(([val, label]) => (
                            <button
                              key={val}
                              type="button"
                              className={`btn btn-sm ${currentFeedback.feedbackPrefab[2] === val ? "btn-primary" : "btn-outline-secondary"}`}
                              onClick={() => handleFeedbackPrefabChange("orientation", val)}
                            >
                              {label}
                            </button>
                          ))}
                        </div>

                        {/* distance bool */}
                        <div className="d-flex align-items-center gap-2">
                          <small className="text-secondary">Distance :</small>
                          <button
                            type="button"
                            className={`btn btn-sm ${currentFeedback.feedbackPrefab.includes("distance") ? "btn-primary" : "btn-outline-secondary"}`}
                            onClick={() => handleFeedbackPrefabChange("distance", !currentFeedback.feedbackPrefab.includes("distance"))}
                          >
                            {currentFeedback.feedbackPrefab.includes("distance") ? "✔ Oui" : "Non"}
                          </button>
                        </div>

                      </div>
                    )}

                  </div>
                  <div alt="Echelle" className="col-md-2"
                    title="Echelle à appliquer pour réduire ou agrandir l'indicateur">
                    <TextInput
                      label="Échelle"
                      type="text"
                      value={currentFeedback.feedbackPrefabScale}
                      onChange={(e) => {
                        // Nettoyage : garder uniquement chiffres et point
                        let cleaned = e.target.value.replace(/[^0-9.]/g, "");
                        // Éviter plusieurs points
                        cleaned = cleaned.replace(/(\..*)\./g, '$1');
                        handleFeedbackPrefabChange(
                          "feedbackPrefabScale", cleaned);
                      }}
                    />
                  </div>

                  {/* Position / Rotation — visibles uniquement si un indicateur est sélectionné */}
                  {currentFeedback.feedbackPrefab.length > 0 && (
                    <div className="col-md-12 mt-3">

                      {/* Bouton burger */}
                      <button
                        type="button"
                        className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center"
                        onClick={() => setShowTransformMenu(!showTransformMenu)}
                      >
                        <span>
                          <i className="mdi mdi-cog-outline me-2" />
                          Position & Rotation
                        </span>
                        <i className={`mdi ${showTransformMenu ? "mdi-chevron-up" : "mdi-chevron-down"}`} />
                      </button>

                      {/* Menu collapsible */}
                      {showTransformMenu && (
                        <div className="mt-3 p-3 border rounded bg-light">
                          <div className="row g-3">

                            {/* POSITION */}
                            <div className="col-md-6">
                              <label className="form-label small fw-bold">
                                <i className="mdi mdi-axis-arrow" /> Position (x, y, z)
                              </label>
                              <div className="d-flex gap-2">
                                {["x", "y", "z"].map((axis) => (
                                  <div key={axis} className="flex-fill">
                                    <TextInput
                                      label={axis.toUpperCase()}
                                      type="text"
                                      value={String(currentFeedback.feedbackPos?.[axis] ?? 0)}
                                      onChange={(e) =>
                                        setCurrentFeedback(prev => ({
                                          ...prev,
                                          feedbackPos: { ...prev.feedbackPos, [axis]: e.target.value }
                                        }))
                                      }
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* ROTATION */}
                            <div className="col-md-6">
                              <label className="form-label small fw-bold">
                                <i className="mdi mdi-rotate-3d" /> Rotation (x, y, z)
                              </label>
                              <div className="d-flex gap-2">
                                {["x", "y", "z"].map((axis) => (
                                  <div key={axis} className="flex-fill">
                                    <TextInput
                                      label={axis.toUpperCase()}
                                      type="text"
                                      value={String(currentFeedback.feedbackRot?.[axis] ?? 0)}
                                      onChange={(e) =>
                                        setCurrentFeedback(prev => ({
                                          ...prev,
                                          feedbackRot: { ...prev.feedbackRot, [axis]: e.target.value }
                                        }))
                                      }
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>

                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div alt="Ajouter/modifier l'indicateur" className="col d-flex justify-content-center my-3">
                    {editingFeedback == true ? (
                      <div>
                        <button alt="Annuler la modification"
                          type="button"
                          className="btn btn-sm btn-outline-secondary mt-3 me-2"
                          title="Annuler la modification"
                          onClick={handleCancelFeedbackEdit}
                        >
                          Annuler
                        </button>
                        <button alt="Modifier l'indicateur'"
                          type="button"
                          title="Modifier l'indicateur"
                          className="btn btn-sm btn-outline-primary mt-3"
                          onClick={() => { handleAddFeedback(); }}
                          disabled={
                            !currentFeedback.feedbackPrefab ||
                            currentFeedback.feedbackPrefab.length === 0 ||
                            (currentFeedback.feedbackPrefab[0] === "point" && (!currentFeedback.feedbackPrefab[1] || !currentFeedback.feedbackPrefab[2]))
                          }
                        >
                          {currentFeedback.feedbackPrefab[0] === "point" && !currentFeedback.feedbackPrefab[1]
                            ? "◀ Choisir une direction"
                            : currentFeedback.feedbackPrefab[0] === "point" && !currentFeedback.feedbackPrefab[2]
                              ? "◀ Choisir une orientation"
                              : "Modifier"
                          }
                        </button></div>
                    ) : (
                      <button alt="Ajouter l'indicateur'"
                        type="button"
                        className="btn btn-sm btn-outline-primary mt-3"
                        onClick={handleAddFeedback}
                        disabled={
                          !currentFeedback.feedbackPrefab ||
                          currentFeedback.feedbackPrefab.length === 0 ||
                          (currentFeedback.feedbackPrefab[0] === "point" && (!currentFeedback.feedbackPrefab[1] || !currentFeedback.feedbackPrefab[2]))
                        }
                      >
                        {(!currentFeedback.feedbackPrefab || currentFeedback.feedbackPrefab.length === 0)
                          ? "◀ Choisir une forme"
                          : currentFeedback.feedbackPrefab[0] === "point" && !currentFeedback.feedbackPrefab[1]
                            ? "◀ Choisir une direction"
                            : currentFeedback.feedbackPrefab[0] === "point" && !currentFeedback.feedbackPrefab[2]
                              ? "◀ Choisir une orientation"
                              : "+ Ajouter l'indicateur ▶"
                        }
                      </button>
                    )}
                  </div>
                </div>
                <p alt="texte d'aide" className="small text-secondary text-center">Les indicateurs (feedbacks) sont les éléments visuels, placés dans l'espace.<br />
                  Ils permettent d'indiquer le ou les emplacements de l'action à réaliser simultanément<br />(ex: extraire un élément en attrapant les 2 poignées.)
                </p>
              </div>
              <div className="col" alt="right">
                <div alt="liste des indicateurs" className="col">
                  {/* Liste des feedbacks ajoutés */}
                  {Array.isArray(currentAction.actionFeedbacks) &&
                    currentAction.actionFeedbacks.length > 0 ? (
                    <div >
                      <label>Liste actuelle</label>
                      {currentAction.actionFeedbacks.map((fb, idx) => (
                        <div
                          key={idx}
                          className="border rounded p-2 mb-1 d-flex align-items-center justify-content-start"
                        >
                          <button alt="Modifier cet indicateur"
                            type="button"
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => {
                              handleEditFeedback(idx);
                            }}
                            title="MODIFIER l'indicateur"
                          >
                            <FaEdit />
                          </button>
                          <button alt="supprimer l'indicateur"
                            type="button"
                            className="btn btn-sm btn-outline-danger me-2"
                            onClick={() => handleRemoveFeedback(idx)}
                            title="SUPPRIMER l'indicateur"
                          >
                            <FaTrash />
                          </button>
                          <div>
                            <span className="me-2">
                              Forme : <b>{Array.isArray(fb.feedbackPrefab) ? fb.feedbackPrefab.join(" › ") : fb.feedbackPrefab}</b>
                            </span>
                            <span className="me-2">
                              Échelle {fb.feedbackPrefabScale} : 1
                            </span>
                            <br />
                            <span className="me-2 small text-secondary">
                              Position : [{fb.feedbackPos.x},{" "}
                              {fb.feedbackPos.y}, {fb.feedbackPos.z}]
                            </span>
                            <span className="me-2 small text-secondary">
                              Rotation : [{fb.feedbackRot.x},{" "}
                              {fb.feedbackRot.y}, {fb.feedbackRot.z}]
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div alt="liste vide">
                      <label>Pas d'action...</label>
                      <p className="small text-secondary">◀ Pour ajouter une action spatialisée, sélectionnez un type d'indicateur et ajoutez-le à la liste.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div alt="Annuler / Sauvegarder" className="row mt-4">
            {currentAction.actionTitle ? (
              <div className="d-flex justify-content-center gap-3" alt="Enregistrer la liste">
                <button
                  type="button"
                  className="btn btn-md btn-outline-secondary"
                  onClick={handleCancelAction}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-md btn-outline-primary"
                  onClick={handleAddAction}
                >
                  Enregistrer l'action [{currentAction.actionTitle}]
                </button>
              </div>
            ) : (
              <div className="text-center">
                <button
                  className="btn btn-md btn-outline-primary"
                  onClick={null}
                  disabled={true}
                >
                  ⚠️ Titre de l'action manquant ⚠️
                </button>
                <br />
                <span className="small">
                  Pour sauvegarder <b>un titre</b> est nécessaire.
                </span>
              </div>
            )}
            <div className="text-center mt-2">
              <span className="small text-secondary">
                ⚠️ Enregistrez ou annulez l'action en cours avant de sauvegarder l'étape (sinon vos modifications seront perdues). ⚠️
              </span>
            </div>

          </div>
        </div>
      </details>
      {isImgModalOpen && (
        <div
          onClick={closeImgModal}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "zoom-out"
          }}
        >
          <img
            src={imgModalSrc}
            alt="Aperçu grand format"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: "8px",
              boxShadow: "0 0 30px rgba(0,0,0,0.6)"
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

    </div>
  );
};
export default ActionManage;


//Creators : Florian Cohen-Joly @3DDGAMES -- Under license